import React, { useState, useEffect } from 'react';
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  AcademicCapIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  DocumentTextIcon,
  ClockIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { useToast } from '../components/ToastProvider';
import PageHeader from '../components/PageHeader';
import '../styles/forms.css';

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    re_new_password: '',
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const { notify } = useToast();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    bio: '',
    specialization: '',
    experience_years: '',
  });

  useEffect(() => {
    fetchProfile();
    // Open change-password modal if navigated with hash or query
    try {
      if (window?.location?.hash === '#change-password') {
        setChangingPassword(true);
      } else {
        const params = new URLSearchParams(window.location.search);
        if (params.get('changePassword') === 'true') {
          setChangingPassword(true);
        }
      }
    } catch (e) {}
  }, []);

  const fetchProfile = async () => {
    try {
      const data = await apiService.getProfile();
      console.log('Profile API response:', data); // Debug log
      setProfile(data);
      
      // Handle nested profile data structure
      const teacherProfile = data.profile || {};
      setFormData({
        first_name: data.first_name || teacherProfile.full_name?.split(' ')[0] || '',
        last_name: data.last_name || teacherProfile.full_name?.split(' ').slice(1).join(' ') || '',
        email: data.email || '',
        phone: data.phone || teacherProfile.phone || '',
        bio: teacherProfile.bio || '',
        specialization: teacherProfile.specialization || '',
        experience_years: teacherProfile.experience_years || '',
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiService.updateProfile(formData);
      setEditing(false);
      fetchProfile();
      notify('Profile updated successfully', { type: 'success' });
    } catch (error) {
      console.error('Failed to update profile:', error);
      notify('Failed to update profile', { type: 'error' });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCancel = () => {
    const teacherProfile = profile.profile || {};
    setFormData({
      first_name: profile.first_name || teacherProfile.full_name?.split(' ')[0] || '',
      last_name: profile.last_name || teacherProfile.full_name?.split(' ').slice(1).join(' ') || '',
      email: profile.email || '',
      phone: profile.phone || teacherProfile.phone || '',
      bio: teacherProfile.bio || '',
      specialization: teacherProfile.specialization || '',
      experience_years: teacherProfile.experience_years || '',
    });
    setEditing(false);
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
    if (passwordError) setPasswordError('');
    if (passwordSuccess) setPasswordSuccess('');
  };

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (passwordForm.new_password !== passwordForm.re_new_password) {
      setPasswordError('New passwords do not match.');
      return;
    }
    setPasswordLoading(true);
    try {
      await apiService.changePassword(passwordForm);
      setPasswordSuccess('Password updated successfully.');
      setPasswordForm({ current_password: '', new_password: '', re_new_password: '' });
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setChangingPassword(false);
      notify('Password changed successfully', { type: 'success' });
    } catch (err) {
      // Extract error message from response
      let errorMessage = 'Failed to change password.';
      if (err.message) {
        errorMessage = err.message;
      } else if (err.response && err.response.data) {
        // Handle validation errors from backend
        const errors = err.response.data;
        if (errors.old_password) {
          errorMessage = Array.isArray(errors.old_password) 
            ? errors.old_password[0] 
            : errors.old_password;
        } else if (errors.detail) {
          errorMessage = errors.detail;
        } else if (errors.new_password) {
          errorMessage = Array.isArray(errors.new_password) 
            ? errors.new_password[0] 
            : errors.new_password;
        }
      }
      setPasswordError(errorMessage);
      notify(errorMessage, { type: 'error' });
    } finally {
      setPasswordLoading(false);
    }
  };

  // Check if passwords match
  const passwordsMatch = passwordForm.new_password && 
    passwordForm.re_new_password && 
    passwordForm.new_password === passwordForm.re_new_password;
  
  const passwordsDontMatch = passwordForm.new_password && 
    passwordForm.re_new_password && 
    passwordForm.new_password !== passwordForm.re_new_password;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-6">
        {/* Aitech Logo Animation */}
        <div className="relative">
          <div className="w-20 h-20 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg animate-pulse">
            <span className="text-2xl font-bold text-white">A</span>
          </div>
          <div className="absolute -inset-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl blur opacity-30 animate-pulse"></div>
        </div>
        
        {/* Loading Text */}
        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Loading Profile</h3>
          <p className="text-sm text-gray-500">Please wait while we fetch your information...</p>
        </div>
        
        {/* Modern Loading Animation */}
        <div className="flex space-x-2">
          <div className="w-3 h-3 bg-indigo-600 rounded-full animate-bounce"></div>
          <div className="w-3 h-3 bg-indigo-600 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
          <div className="w-3 h-3 bg-indigo-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
        </div>
        
        {/* Progress Bar */}
        <div className="w-64 bg-gray-200 rounded-full h-2 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Profile"
        subtitle="Manage your account information and preferences"
        actions={!editing ? (
          <>
            <button
              onClick={() => setChangingPassword(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Change Password
            </button>
            <button
              onClick={() => setEditing(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Edit Profile
            </button>
          </>
        ) : null}
      />

      {/* Profile Information */}
      <div className="card">
        <div className="px-4 py-5 sm:p-6">

          {editing ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="form-label">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="form-label">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="form-label">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="form-label">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="form-label">
                    Specialization
                  </label>
                  <input
                    type="text"
                    name="specialization"
                    value={formData.specialization}
                    onChange={handleChange}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="form-label">
                    Years of Experience
                  </label>
                  <input
                    type="number"
                    name="experience_years"
                    value={formData.experience_years}
                    onChange={handleChange}
                    min="0"
                    className="input-field"
                  />
                </div>
              </div>

              <div>
                                  <label className="form-label">
                    Bio
                  </label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows={4}
                  className="input-field"
                />
              </div>

              <div className="flex justify-between space-x-3">
                <button
                  type="button"
                  onClick={() => setChangingPassword(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Change Password
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <XMarkIcon className="h-4 w-4 mr-2" />
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Save Changes
                </button>
              </div>
            </form>
          ) : null}
        </div>
      </div>

      {/* Sectioned Details */}
      {!editing && (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          {/* Contact */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <UserIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Name:</p>
                    <p className="text-sm text-gray-900">
                      {profile?.first_name || profile?.profile?.full_name?.split(' ')[0] || 'Not provided'} {profile?.last_name || profile?.profile?.full_name?.split(' ').slice(1).join(' ') || ''}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <EnvelopeIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Email:</p>
                    <p className="text-sm text-gray-900">{profile?.email || 'Not provided'}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <PhoneIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Phone:</p>
                    <p className="text-sm text-gray-900">{profile?.phone || profile?.profile?.phone || 'Not provided'}</p>
                  </div>
                </div>
              </div>
              <div className="pt-2">
                <button
                  onClick={() => setChangingPassword(true)}
                  className="w-full inline-flex items-center justify-center px-3 py-2 text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Change Password
                </button>
              </div>
            </div>
          </div>

          {/* Professional */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Professional</h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <AcademicCapIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Specialization</p>
                  <p className="text-sm text-gray-500">{profile?.profile?.specialization || 'Not specified'}</p>
                </div>
              </div>
              <div className="flex items-center">
                <ClockIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Experience</p>
                  <p className="text-sm text-gray-500">{profile?.profile?.experience_years ? `${profile.profile.experience_years} years` : 'Not specified'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* About */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">About</h3>
            <div className="flex items-start">
              <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
              <p className="text-sm text-gray-600">{profile?.profile?.bio || 'No bio added yet.'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {changingPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-900">Change Password</h3>
              <button
                onClick={() => {
                  setChangingPassword(false);
                  setPasswordForm({ current_password: '', new_password: '', re_new_password: '' });
                  setPasswordError('');
                  setPasswordSuccess('');
                  setShowCurrentPassword(false);
                  setShowNewPassword(false);
                  setShowConfirmPassword(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={submitPasswordChange} className="space-y-3">
              {passwordError && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-md p-2">
                  {passwordSuccess}
                </div>
              )}
              <div>
                <label className="form-label">Current Password</label>
                <div className="relative">
                <input
                    type={showCurrentPassword ? 'text' : 'password'}
                  name="current_password"
                  value={passwordForm.current_password}
                  onChange={handlePasswordChange}
                  required
                    disabled={passwordLoading}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showCurrentPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label className="form-label">New Password</label>
                <div className="relative">
                <input
                    type={showNewPassword ? 'text' : 'password'}
                  name="new_password"
                  value={passwordForm.new_password}
                  onChange={handlePasswordChange}
                  required
                    disabled={passwordLoading}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                      passwordsDontMatch ? 'border-red-300' : passwordsMatch ? 'border-green-300' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">Minimum 8 characters</p>
              </div>
              <div>
                <label className="form-label">Confirm New Password</label>
                <div className="relative">
                <input
                    type={showConfirmPassword ? 'text' : 'password'}
                  name="re_new_password"
                  value={passwordForm.re_new_password}
                  onChange={handlePasswordChange}
                  required
                    disabled={passwordLoading}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                      passwordsDontMatch ? 'border-red-300' : passwordsMatch ? 'border-green-300' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {passwordsMatch && (
                  <p className="mt-1 text-xs text-green-600 flex items-center">
                    <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Passwords match!
                  </p>
                )}
                {passwordsDontMatch && (
                  <p className="mt-1 text-xs text-red-600 flex items-center">
                    <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    Passwords do not match
                  </p>
                )}
              </div>
              <div className="flex justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setChangingPassword(false);
                    setPasswordForm({ current_password: '', new_password: '', re_new_password: '' });
                    setPasswordError('');
                    setPasswordSuccess('');
                    setShowCurrentPassword(false);
                    setShowNewPassword(false);
                    setShowConfirmPassword(false);
                  }}
                  disabled={passwordLoading}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={passwordLoading || passwordsDontMatch}
                  className="px-3 py-1.5 text-sm rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {passwordLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile; 